/*
 * FAZAYA - Complimentary Discovery (GWP)
 * ---------------------------------------------------------------------------
 * ENTITLEMENT BASIS - UNIT BASED (approved 13 Aug 2026).
 * Supersedes the earlier distinct-identity rule.
 *
 *   U = total eligible fragrance UNITS in cart (quantities summed)
 *   samples = min(U, max_samples, number of GWP references available)
 *
 *   1 x Sugar 50 ml            -> 1 sample
 *   3 x Sugar 50 ml            -> 3 samples
 *   4 x Sugar 50 ml            -> 3 samples (capped by max_samples)
 *   Sugar + Osman              -> 2 samples
 *   Sugar + Osman + Smoked V.  -> 3 samples
 *
 * SELECTION - DISCOVERY FIRST (unchanged):
 *   Undiscovered references by priority, then already-purchased references by
 *   priority. Never a duplicate reference while an unused one remains, so the
 *   sample count is also bounded by the size of the GWP catalogue.
 *   Priority: Osman 1, Smoked Vanilla 2, Sugar 3 (configurable).
 *
 * CONVERGENCE:
 *   Every cycle recomputes expected state from the cart alone and forces the
 *   cart to match exactly - references AND quantities. Prior GWP state is
 *   never trusted as input.
 *
 * A line is MANAGED if it carries the GWP property OR its variant appears in
 * the GWP catalogue. The second test closes the case where a GWP variant is
 * inserted directly without the property.
 *
 * Fail-safe: every failure path leaves the cart untouched and lets the
 * customer check out. A GWP must never block or delay an order.
 */
(function () {
  'use strict';

  var CFG = null, busy = false, queued = false, timer = null;
  var PROP = '_fazaya_gwp';
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var WRITES = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];
  var STYLE_ID = 'fazaya-gwp-style';
  var lastCss = '';
  var badgeBusy = false;
  var cycleGuard = 0;

  function loadConfig() {
    try {
      var el = document.getElementById('fazaya-gwp-config');
      if (!el) return null;
      var c = JSON.parse(el.textContent);
      if (!c || c.enabled !== true) return null;
      if (!c.catalogue || !c.catalogue.length) return null;
      if (!c.eligible_variants) return null;
      return c;
    } catch (e) { return null; }
  }

  function isCartWrite(url) {
    url = String(url || '');
    for (var i = 0; i < WRITES.length; i++) { if (url.indexOf(WRITES[i]) !== -1) return true; }
    return false;
  }

  function byPriority(a, b) { return (a.priority || 999) - (b.priority || 999); }
  function hasGwpProp(i) { return !!(i.properties && i.properties[PROP]); }

  function catalogueEntry(variantId) {
    for (var i = 0; i < CFG.catalogue.length; i++) {
      if (Number(CFG.catalogue[i].variant_id) === Number(variantId)) return CFG.catalogue[i];
    }
    return null;
  }

  function isManaged(i) { return hasGwpProp(i) || !!catalogueEntry(i.variant_id); }

  function identityOf(variantId) {
    var list = CFG.eligible_variants;
    for (var i = 0; i < list.length; i++) {
      if (Number(list[i].variant_id) === Number(variantId)) return list[i].fragrance_id;
    }
    return null;
  }

  function getCart() {
    return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('cart read failed'); return r.json(); });
  }

  /* ================= ENTITLEMENT - UNIT BASED ================= */

  function computeAllocation(cart) {
    var units = 0;
    var purchased = [];

    cart.items.forEach(function (item) {
      if (isManaged(item)) return;
      var id = identityOf(item.variant_id);
      if (!id) return;
      units += item.quantity;
      if (purchased.indexOf(id) === -1) purchased.push(id);
    });

    if (units < 1) return [];

    var cat = CFG.catalogue.slice().sort(byPriority);

    var poolA = cat.filter(function (c) { return purchased.indexOf(c.fragrance_id) === -1; });
    var poolB = cat.filter(function (c) { return purchased.indexOf(c.fragrance_id) !== -1; });
    var ordered = poolA.concat(poolB);

    if (CFG.respect_inventory === true && CFG.reserve_floor !== null) {
      ordered = ordered.filter(function (c) {
        return typeof c.available !== 'number' || c.available > Number(CFG.reserve_floor);
      });
    }

    var cap = Number(CFG.max_samples) || 3;
    var count = Math.min(units, cap, ordered.length);

    return ordered.slice(0, count);
  }

  /* ================= CONVERGENCE ================= */

  function diff(cart) {
    var desired = computeAllocation(cart).map(function (d) { return Number(d.variant_id); });
    var satisfied = {};
    var updates = {};
    var converged = true;

    cart.items.forEach(function (item) {
      if (!isManaged(item)) return;

      var vid = Number(item.variant_id);
      var target = 0;

      if (desired.indexOf(vid) !== -1 && hasGwpProp(item) && !satisfied[vid]) {
        target = 1;
        satisfied[vid] = true;
      }

      if (item.quantity !== target) {
        updates[item.key] = target;
        converged = false;
      }
    });

    var adds = desired.filter(function (vid) { return !satisfied[vid]; });
    if (adds.length) converged = false;

    return { updates: updates, adds: adds, converged: converged };
  }

  function applyUpdates(updates) {
    if (!Object.keys(updates).length) return Promise.resolve();
    return fetch(ROOT + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { return r.json(); });
  }

  function applyAdds(adds) {
    if (!adds.length) return Promise.resolve();
    var items = adds.map(function (vid) {
      var p = {}; p[PROP] = 'true';
      return { id: vid, quantity: 1, properties: p };
    });
    return fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) {
        return r.json().then(function (e) {
          console.warn('[FAZAYA GWP] allocation skipped:', e && e.description);
        });
      }
      return r.json();
    });
  }

  function refreshUI() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    try {
      if (window.FoxTheme && window.FoxTheme.pubsub && window.FoxTheme.pubsub.publish) {
        window.FoxTheme.pubsub.publish('cartUpdate', { source: 'fazaya-gwp' });
      }
    } catch (e) {}
  }

  /* ================= PRESENTATION ================= */

  function esc(s) { return String(s || '').split('"').join('').split('\\').join(''); }

  function buildCss(cart) {
    var rules = [];
    var heading = esc(CFG.heading || 'Complimentary Discovery');
    var subline = esc(CFG.subline || 'Selected for you by FAZAYA');

    cart.items.forEach(function (item, idx) {
      if (!isManaged(item)) return;
      var entry = catalogueEntry(item.variant_id);
      var label = esc(entry ? (entry.label + ' \u00b7 ' + (entry.size || '2 ml')) : 'Complimentary sample');
      var n = idx + 1;

      ['#CartDrawer-Item-' + n, '#CartItem-' + n].forEach(function (id) {
        rules.push(id + ' quantity-input,' + id + ' .cart-quantity,' + id + ' .quantity,' +
          id + ' .btn-remove,' + id + ' .cart-item__remove,' +
          id + ' .cart-item__quantity-wrapper,' + id + ' foxkit-incart-upsell,' +
          id + ' .volume-pricing-toggle{display:none!important}');
        rules.push(id + ' .cart-item__title{font-size:0!important;line-height:0!important;' +
          'pointer-events:none!important;text-decoration:none!important}');
        rules.push(id + ' .cart-item__title::before{content:"' + heading + '";display:block;' +
          'font-size:.66rem;line-height:1.45;letter-spacing:.16em;text-transform:uppercase;' +
          'opacity:.7;margin-bottom:.2em}');
        rules.push(id + ' .cart-item__title::after{content:"' + label + '";display:block;' +
          'font-size:.95rem;line-height:1.35;letter-spacing:.01em}');
        rules.push(id + ' .cart-item__options{font-size:0!important;line-height:0!important}');
        rules.push(id + ' .cart-item__options::after{content:"' + subline + '";display:block;' +
          'font-size:.7rem;line-height:1.45;font-style:italic;opacity:.5;margin-top:.2em}');
        rules.push(id + ' .price,' + id + ' .cart-item__total{opacity:.5}');
      });
    });

    return rules.join('\n');
  }

  function applyCss(cart) {
    var css = buildCss(cart);
    if (css === lastCss) return;
    lastCss = css;
    var el = document.getElementById(STYLE_ID);
    if (!el) {
      el = document.createElement('style');
      el.id = STYLE_ID;
      document.head.appendChild(el);
    }
    el.textContent = css;
  }

  function applyBadge(cart) {
    if (CFG.badge_excludes_gwp !== true) return;
    if (badgeBusy) return;

    var gwpUnits = 0;
    cart.items.forEach(function (i) { if (isManaged(i)) gwpUnits += i.quantity; });
    var commercial = Math.max(0, cart.item_count - gwpUnits);

    badgeBusy = true;
    try {
      var nodes = document.querySelectorAll('cart-count');
      Array.prototype.forEach.call(nodes, function (n) {
        var text = n.getAttribute('data-type') === 'blank' ? '(' + commercial + ')' : String(commercial);
        var current = n.textContent.split(' ').join('').split('\n').join('');
        if (current !== text) n.textContent = text;
        if (commercial === 0) { n.setAttribute('hidden', ''); } else { n.removeAttribute('hidden'); }
      });
    } catch (e) {
      console.warn('[FAZAYA GWP] badge update skipped:', e);
    }
    setTimeout(function () { badgeBusy = false; }, 30);
  }

  /* ================= RUNTIME ================= */

  function paint() {
    if (!CFG) return Promise.resolve();
    return getCart().then(function (cart) {
      applyCss(cart);
      applyBadge(cart);
    }).catch(function () {});
  }

  function reconcile() {
    if (!CFG) return Promise.resolve();
    if (busy) { queued = true; return Promise.resolve(); }
    busy = true;

    return getCart().then(function (cart) {
      var d = diff(cart);

      if (d.converged) {
        cycleGuard = 0;
        applyCss(cart);
        applyBadge(cart);
        return;
      }

      cycleGuard += 1;
      if (cycleGuard > 4) {
        console.warn('[FAZAYA GWP] convergence abandoned after repeated attempts');
        return paint();
      }

      return applyUpdates(d.updates)
        .then(function () { return applyAdds(d.adds); })
        .then(refreshUI)
        .then(paint)
        .then(function () { queued = true; });
    }).catch(function (e) {
      console.warn('[FAZAYA GWP] reconcile failed, cart untouched:', e);
    }).then(function () {
      busy = false;
      if (queued) { queued = false; setTimeout(reconcile, 200); }
    });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(function () { cycleGuard = 0; reconcile(); }, 300);
  }

  function patchFetch() {
    var nativeFetch = window.fetch;
    if (!nativeFetch || nativeFetch.__fzGwp) return;
    var patched = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var write = isCartWrite(url);
      return nativeFetch.apply(this, arguments).then(function (res) {
        if (write && !busy) schedule();
        return res;
      });
    };
    patched.__fzGwp = true;
    window.fetch = patched;
  }

  function patchXhr() {
    var open = XMLHttpRequest.prototype.open;
    if (open.__fzGwp) return;
    var po = function (m, url) { this.__fzWrite = isCartWrite(url); return open.apply(this, arguments); };
    po.__fzGwp = true;
    XMLHttpRequest.prototype.open = po;
    var send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      if (this.__fzWrite) {
        this.addEventListener('load', function () { if (!busy) schedule(); });
      }
      return send.apply(this, arguments);
    };
  }

  function init() {
    CFG = loadConfig();
    if (!CFG) return;

    patchFetch();
    patchXhr();

    document.addEventListener('cart:updated', function () { setTimeout(paint, 80); });
    document.addEventListener('cart:refresh', function () { setTimeout(paint, 80); });

    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (!badgeBusy) { clearTimeout(window.__fzPaintT); window.__fzPaintT = setTimeout(paint, 120); }
      }).observe(document.body, { childList: true, subtree: true });
    }

    reconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }
})();
